import { setAndSendReadingToWS, clearState } from "@app/routes/activities/reading/store/readingSlice";
import { GAME_TYPES } from "@amplio-common/vocabulary/constants";
import { getCategoryFilterObject, getIsCategoryFilter, getIsSoundFilter, getSoundFilterObject } from "@app/routes/vocabulary/serializers";
import { fetchManualGame, fetchReadingGame, fetchSpellingGame, generateGame } from "@app/routes/vocabulary/store/vocabularyThunks";
import { trackTreatmentV3Event } from "@app/store/analytics/treatmentV3";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { contentUpdateAllStudents, contentUpdateStudentStatus, selectLastVisitedLibrary, selectPreviewBoardMode, setAssignInfoStudentId, setCustomFilterStudentList, setIsActivityPreviewPressed, setIsGameCustomFilterOpen, setIsPresenting, setIsShowAssignInfoPopper, setLastPinnedBoard, setPinnedBoard, setPreviewBoardMode } from "./contentSlice";
import { h5pGamesUpdateStudentStatus, selectPreviewH5PGame, setPreviewListUUID, setPreviewH5PGame, selectPreviewListUUID } from "./h5pGamesSlice";
import { SLICE_NAMES } from "./sliceNames";
import { speechUpdateStudentStatus } from "./speechSlice";
import { VIDEO_SESSION_PAGES as VSP } from "@app/common/data/ActivitiessEnums";
import { selectSelectedActivity, setPreviewSelectedActivity, studioUpdateAllStudents } from "./studioSlice";
import { setArticulationWord } from "./studentSlice";
import { articulationUpdateStudentStatus, selectPreviewWords, sendWordToStudent } from "./articulationSlice";
import { selectVideoId, youtubeUpdateAllStudents } from "./youtubeSlice";
import { selectConnectedStudentsIds } from "./websocketSlice";
import { setPreviewH5PGameExternal } from "@app/common/calendar/components/createSession/V3/store/CreateSessionSlice";
import { selectCategories, selectGamesRelations, setSelectedGame, selectNumOfSyllables, selectSelectedArticulationSoundPositions, selectSelectedArticulationSounds, selectSelectedCategories, selectSelectedFilter, selectSounds, selectCurrentActivity, selectDoneActivities, setCurrentActivity, setDoneActivities, saveTreatmentGamePlayList, selectFormattedGamePlayList } from "./activityLibrarySlice";
import { PREVIEW_BOARD_ID } from "../constants";
import { selectUserStatuses } from "./treatmentServerDataSlice";
import { ROLES } from "@amplio-common/constants";
import { VIDEO_STATUSES } from "@app/routes/treatment/constants";

const getGameFilterData = (game, state, isCategoryFilter, isSoundFilter) => {
  const sounds = selectSounds(state);
  const selectedArticulationSounds = selectSelectedArticulationSounds(state);
  const selectedArticulationSoundPositions = selectSelectedArticulationSoundPositions(state);
  const numOfSyllables = selectNumOfSyllables(state);
  const categories = selectCategories(state);
  const selectedCategories = selectSelectedCategories(state);
  const articulation = isSoundFilter
    ? getSoundFilterObject(sounds, selectedArticulationSounds, selectedArticulationSoundPositions)
    : { items: [], groups: [], positions: [] };
  const vocabulary = isCategoryFilter ? getCategoryFilterObject(categories, selectedCategories) : { groups: [], sub_categories: [] };
  
  return {
    number_of_syllables: isSoundFilter ? numOfSyllables : undefined,
    articulation,
    vocabulary,
  };
};

const fetchRequiredGame = (dispatch, game, data) => {
  if (game.game_type === GAME_TYPES.READING) {
    dispatch(fetchReadingGame(data));
    return;
  }
  if (game.game_type === GAME_TYPES.SPELLING) {
    dispatch(fetchSpellingGame(data));
    return;
  }
  game.game_url ? dispatch(fetchManualGame(data)) : dispatch(generateGame(data));
};

const openGame = (dispatch, state, game, isCategoryFilter, isSoundFilter, onSuccess) => {
  const filters = isCategoryFilter || isSoundFilter ? getGameFilterData(game, state, isCategoryFilter, isSoundFilter) : undefined;
  const gamesRelations = selectGamesRelations(state);
  const selectedFilter = selectSelectedFilter(state);

  const gameRelation = gamesRelations[`${game?.external_id}_${game?.objective}`];
  const data = {
    game: { ...game, with_custom_filter: true },
    filters,
    areaOfNeed: gameRelation?.area_title || selectedFilter?.areaOfNeedName || selectedFilter?.areaOfNeed,
    protocol: gameRelation?.protocol_title || selectedFilter?.protocolName || selectedFilter?.protocol,
    objective: game?.objective_title,
    successFn: onSuccess,
  };

  fetchRequiredGame(dispatch, game, data);
};

const updatePlaylist = (dispatch, state, game) => {
  dispatch(setCurrentActivity(null));
  const doneActivities = selectDoneActivities(state);
  const prevActivity = selectCurrentActivity(state);
  const listUUID = selectPreviewListUUID(state);
  const pl = selectFormattedGamePlayList(state);
  prevActivity && dispatch(setDoneActivities([...doneActivities, prevActivity]));
  dispatch(setCurrentActivity(listUUID || game?.uuid || null));
  dispatch(setPreviewListUUID(null));

  dispatch(saveTreatmentGamePlayList({ pl })); // persist to db
};

const onH5PAssign = (dispatch, state, h5pGame, selectedStudents, source, listGame) => {
  const previewWords = selectPreviewWords(state);
  const studentsConnected = selectUserStatuses(state)
    .filter(user => user.role === ROLES.STUDENT.id && user.connected && user.video_status === VIDEO_STATUSES.APPROVED).map(user => user.id);
  const relevantSelectedStudented = studentsConnected.length === 1 ? studentsConnected : selectedStudents; //if only on student is connected - assignment should be to him
  studentsConnected.length === 1 && dispatch(setPinnedBoard({ newBoardId: studentsConnected[0], src: "assign-to-one-student" }));
  studentsConnected.length === 1 && dispatch(setLastPinnedBoard(studentsConnected[0]));
  
  dispatch(setAssignInfoStudentId(relevantSelectedStudented[0]));
  dispatch(setIsPresenting(false));
  dispatch(contentUpdateAllStudents(relevantSelectedStudented.map(id => ({ id, changes: { boardMode: VSP.VOCABULARY } }))));
  relevantSelectedStudented.forEach(studentID => {
    if (h5pGame?.game?.game_type === GAME_TYPES.READING) {
      dispatch(h5pGamesUpdateStudentStatus({ id: studentID, changes: { h5pGame: { ...h5pGame, isLoaded: true } } }));
      dispatch(setAndSendReadingToWS({ reducer: clearState, data: { boardId: studentID } }));
    } else {
      dispatch(h5pGamesUpdateStudentStatus({ id: studentID, changes: { h5pGame: { } } })); // workaround to get a reset on the game
      setTimeout(() => dispatch(h5pGamesUpdateStudentStatus({ id: studentID, changes: { h5pGame: { ...h5pGame, isLoaded: false }, isH5PGameDone: false } })), 1); //reduxtoolkit probably make optimiztions when you change the same state and it ignored the first change. That is a workaround.
    }
    dispatch(setPreviewH5PGame({}));
    dispatch(speechUpdateStudentStatus({ id: studentID,
      changes: {
        isSpeechLocked: true,
        speechAnalysisResults: {},
        recordingsCount: 0,
        reset: true,
        recordingStartTime: null,
        isEnabledRecording: false } }));
    // dispatch(articulationUpdateStudentStatus({ id: studentID, changes: { studentWords: [] } }));
    
    if (h5pGame?.game?.is_articulation || h5pGame?.game?.is_naming) {
      dispatch(articulationUpdateStudentStatus({ id: studentID, changes: { studentWords: previewWords } }));
    }

    updatePlaylist(dispatch, state, listGame); // update playlist UI
 
    trackTreatmentV3Event("assign_to_students", state, null, { num_students_chosen: relevantSelectedStudented.length,
      modal_tool: VSP.VOCABULARY,
      activity_id: h5pGame?.game?.id,
      activity_objective: h5pGame?.game?.objective,
      source: "assign",
      assign_source: source });
  });
};

export const setBoardMode = createAsyncThunk(
  `${SLICE_NAMES.CONTENT_SLICE}/setBoardMode`,
  async (payload, thunkAPI) => {
    const { dispatch } = thunkAPI;
    const { id } = payload;
    dispatch(contentUpdateStudentStatus({ id, changes: { boardMode: VSP.SESSION_START } }));
  },
);

export const activityPressAction = createAsyncThunk(
  `${SLICE_NAMES.CONTENT_SLICE}/startGamePressed`,
  async (payload, thunkAPI) => {
    const { dispatch } = thunkAPI;
    const state = thunkAPI.getState();

    const { game, index, isPreview, fromFilterDialog, selectedStudents, isExternal, source, isFromList } = payload;

    dispatch(setIsActivityPreviewPressed(isPreview));
    dispatch(setSelectedGame({ ...game, game_type: index + 1, source }));
    const isCategoryFilter = getIsCategoryFilter(game);
    const isSoundFilter = getIsSoundFilter(game);

    isPreview && isFromList && dispatch(setPreviewListUUID(game?.uuid));

    //if game require more settings from user
    if (![GAME_TYPES.READING, GAME_TYPES.SPELLING].includes(game.game_type) && (isCategoryFilter || isSoundFilter) && !fromFilterDialog) {
      dispatch(setCustomFilterStudentList(selectedStudents));
      dispatch(setIsGameCustomFilterOpen(true));
      dispatch(setIsShowAssignInfoPopper(false));
      return;
    }

    !isPreview && selectedStudents.length > 1 && dispatch(setIsShowAssignInfoPopper(true));
    isPreview && dispatch(setPinnedBoard({ newBoardId: PREVIEW_BOARD_ID, src: "preview-pressed" }));
    
    const onAssignSuccess = (measureResponse, gameResponse, gameProps, filters, area) => {
      const h5pGame = { id: gameResponse?.id, ...measureResponse, isLoaded: false, game: gameProps, filters, area };
      onH5PAssign(dispatch, state, h5pGame, selectedStudents, source, game);
    };

    const onPreviewSuccess = (measureResponse, gameResponse, gameProps, filters, area) => {
      const h5pGame = { id: gameResponse?.id, ...measureResponse, isLoaded: false, game: gameProps, filters, area };
      !isExternal ? dispatch(setPreviewH5PGame(h5pGame)) : dispatch(setPreviewH5PGameExternal({ h5pGame, source }));
    };

    const callbackFunc = isPreview ? onPreviewSuccess : onAssignSuccess;
    openGame(dispatch, state, game, isCategoryFilter, isSoundFilter, callbackFunc);
  },
);

export const assignGeneral = createAsyncThunk(
  `${SLICE_NAMES.CONTENT_SLICE}/assignGeneral`,
  async (payload, thunkAPI) => {
    const { dispatch } = thunkAPI;
    const { selectedStudentIds } = payload;
    const state = thunkAPI.getState();
    const previewBoardMode = selectPreviewBoardMode(state);
    const selectedStudioActivity = selectSelectedActivity(state);
    const connectedStudents = selectConnectedStudentsIds(true)(state);
    const youtubeVideoId = selectVideoId(state);
    const previewH5PGame = selectPreviewH5PGame(state);
    const lastVisitedLibrary = selectLastVisitedLibrary(state);
    const listUUID = selectPreviewListUUID(state);

    previewBoardMode === VSP.ACTIVITY && dispatch(studioUpdateAllStudents(selectedStudentIds.map(id => ({ id, changes: { selectedActivity: selectedStudioActivity } }))));
    previewBoardMode === VSP.VOCABULARY && onH5PAssign(dispatch, state, previewH5PGame, selectedStudentIds, "preview");
    previewBoardMode === VSP.YOUTUBE && dispatch(youtubeUpdateAllStudents(selectedStudentIds.map(id => ({ id, changes: { videoId: youtubeVideoId } }))));

    dispatch(setAssignInfoStudentId(selectedStudentIds[0]));
    dispatch(setIsShowAssignInfoPopper(true));

    dispatch(setIsPresenting(false));
    dispatch(contentUpdateAllStudents(selectedStudentIds.map(id => ({ id, changes: { boardMode: previewBoardMode } }))));
    dispatch(sendWordToStudent(null)); //bug fix for articulation word dialog
    dispatch(setArticulationWord(null)); //bug fix for articulation word dialog
    selectedStudentIds.forEach(studentID => {
      dispatch(articulationUpdateStudentStatus({ id: studentID, changes: { isShowArticulationDialog: null, studentSelectedWord: null } }));
    });
    dispatch(setPreviewSelectedActivity(null));
    dispatch(setPreviewBoardMode(lastVisitedLibrary));
    connectedStudents.length === 1 && dispatch(setPinnedBoard({ newBoardId: connectedStudents[0], src: "assign-to-one-student" }));
    connectedStudents.length === 1 && dispatch(setLastPinnedBoard(connectedStudents[0]));
  
    let activityId;
    previewBoardMode === VSP.ACTIVITY && (activityId = selectedStudioActivity?.id);
    previewBoardMode === VSP.VOCABULARY && (activityId = previewH5PGame?.game?.id);
    const activityObjective = previewBoardMode === VSP.VOCABULARY ? previewH5PGame?.game?.objective : undefined;

    trackTreatmentV3Event("assign_to_students", state, null, { num_students_chosen: selectedStudentIds.length,
      modal_tool: previewBoardMode,
      activity_id: activityId,
      source: "preview",
      activity_objective: activityObjective,
      assign_source: listUUID ? "playlist-preview" : "preview" });
  },
);
